/**
 * 기능 : 실시간 방송 시작 전 마이크 접근 권한 안내 모달
 *
 * 서버 릴레이 방식에서는 마이크 스트림이 있어야 브라우저 오디오가 장비로 전달되므로
 * 권한이 없는 상태에서 방송을 시작하면 소리가 나가지 않는다.
 * 권한 상태(prompt / denied / unsupported)에 따라 필요한 조치를 안내한다.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { BtnBlue, BtnGhost } from '../Button';
import { ModalBackground, Modal } from '../../assets/styles/Modal';
import { MicPermissionState, requestMicPermission } from '../../utils/micPermission';

const Description = styled.p`
  padding: 0 1rem 1rem;
  font-size: 0.875rem;
  line-height: 1.5;
  color: var(--text-secondary);
  word-break: keep-all;
  text-align: center;
`;

const HeadTitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;

  > .material-symbols-rounded {
    color: var(--filled-red);
  }
`;

/** 권한 상태별 안내 문구 (granted 상태에서는 모달을 열지 않음) */
const GUIDE_MESSAGE: Record<Exclude<MicPermissionState, 'granted'>, string> = {
  prompt: '실시간 방송을 시작하려면 마이크 접근 권한이 필요합니다. 아래 [마이크 권한 요청] 버튼을 누른 뒤 브라우저 알림창에서 [허용]을 선택해주세요.',
  denied: '브라우저에서 마이크 접근이 차단되어 있습니다. 주소창의 자물쇠 아이콘 > 사이트 설정에서 마이크를 [허용]으로 변경하고 새로고침한 뒤 다시 시도해주세요.',
  unsupported: '현재 접속 환경에서는 마이크를 사용할 수 없습니다. HTTPS 주소로 접속했는지 확인하거나 다른 브라우저를 이용해주세요.',
};

interface IProps {
  permission: Exclude<MicPermissionState, 'granted'>;
  onClose: () => void;
  onGranted: () => void;
}

const MicPermissionModal = ({ permission, onClose, onGranted }: IProps) => {
  const { t } = useTranslation();
  const [isRequesting, setIsRequesting] = useState(false);

  // 브라우저 권한 요청창 호출
  const onClickRequest = async () => {
    setIsRequesting(true);
    const isGranted = await requestMicPermission();
    setIsRequesting(false);

    if (isGranted) {
      onGranted();
      return;
    }
    onClose();
  };

  return (
    <ModalBackground onClick={onClose} role='presentation'>
      <Modal>
        <div
          className='modal confirm'
          role='presentation'
          onClick={(event) => {
            event.stopPropagation();
          }}
        >
          <div className='inputForm-head'>
            <HeadTitle>
              <span className='material-symbols-rounded'>mic_off</span>
              {t('마이크 접근 권한이 필요합니다')}
            </HeadTitle>
          </div>
          <Description>{t(GUIDE_MESSAGE[permission])}</Description>
          <div className='modal-footer flex-end'>
            <BtnGhost onClick={onClose}>{t('닫기')}</BtnGhost>
            {permission === 'prompt' && (
              <BtnBlue type='button' onClick={onClickRequest} disabled={isRequesting}>
                {isRequesting ? t('요청 중...') : t('마이크 권한 요청')}
              </BtnBlue>
            )}
          </div>
        </div>
      </Modal>
    </ModalBackground>
  );
};

export default MicPermissionModal;
